import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as NavConstants from 'constants/NavConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  isWorklistItem,
  WorklistItem,
} from 'nav/expanded/ListingRendererConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {Filter, FilterKey, ListableItem, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {withLoadingScreen} from 'util/Promises';
import {BasicEvent} from 'util/Types';
import ScanIntoWorklistButton from 'worklists/ScanIntoWorklistButton';
import {dispatchUpdateWorklist} from 'worklists/WorklistActions';
import {asWorklistType, WorklistSummary, WorklistTypeToModelTypes} from 'worklists/WorklistConstants';
import WorklistSettingsIcon from 'worklists/WorklistSettingsIcon';
import {resolveItemInWorklistFilter} from 'worklists/WorklistUtil';

import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {
  WorklistItemsSidepanelQuery,
  WorklistItemsSidepanelQueryVariables,
  WorklistItemsSidepanelQuery_worklist_WorklistNode_items as WorklistItemGraphQL,
} from './__generated/WorklistItemsSidepanelQuery';

export function maybeAddWorklistPositionColumn<T>(
  columns: Array<ColumnConfiguration<T>>,
  items: Array<T>
): void {
  if (items.length && isWorklistItem<T>(items[0])) {
    columns.splice(1, 0, {
      column: {
        header: getColumnHeaderFromSortKey(SortKey.SORT_WORKLIST_POSITION_KEY),
        accessorKey: ColumnAccessorKey.POSITION,
        isCollapsible: false,
        isSortable: true,
        sortKey: SortKey.SORT_WORKLIST_POSITION_KEY,
        width: 120,
      },
      render: (item: WorklistItem<T>) => <span className="u-ellipsis-text">{item.worklistItemPosition}</span>,
    });
  }
}

export interface WorklistItemsSidepanelProps {
  selectedWorklistId: string;
  clearSelectedWorklist: () => void;
  isExpanded: boolean;
  isExpanding: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
}

export interface EnhancedProps {
  worklistSummary: WorklistSummary | null;
}

export const WORKLIST_ITEMS_SIDEPANEL_QUERY = gql`
  query WorklistItemsSidepanelQuery($worklistId: StrictString!) @skipAudit @engComponent(name: WORKLISTS) {
    worklist(id: $worklistId) {
      ...WorklistItemsGraphQL
      ...WorklistSummaryGraphQL
    }
  }
  ${getFragmentWrapper('WorklistItems').fragment}
  ${getFragmentWrapper('WorklistSummary').fragment}
`;

export function WorklistItemsSidepanel(
  props: WorklistItemsSidepanelProps & EnhancedProps & SearchProps
): JSX.Element {
  const queryResult = useBenchlingQuery<WorklistItemsSidepanelQuery, WorklistItemsSidepanelQueryVariables>(
    WORKLIST_ITEMS_SIDEPANEL_QUERY,
    {variables: {worklistId: props.selectedWorklistId}}
  );

  let worklistSummary: WorklistSummaryGraphQL | null = null;
  let itemIds: Array<string> | null = null;
  if (queryResult.type === QueryResultType.SUCCESS && !isInaccessible(queryResult.data.worklist)) {
    worklistSummary = queryResult.data.worklist;
    itemIds = accessibleObjs(queryResult.data.worklist.items).map(
      (item: ExcludeInaccessible<WorklistItemGraphQL>) => item.id
    );
  }

  function handleSearch(searchParams: SearchParams): void {
    const newSearchParams = resolveItemInWorklistFilter(
      worklistSummary!.id,
      asWorklistType(worklistSummary!.itemType),
      searchParams
    );
    props.onSearch(newSearchParams);
  }

  function handleUpdateFilters(filters: Array<Filter>): void {
    // TODO(Search): This should be the only event that we need to worry about adding the default
    // worklist filters to. The loading of a worklist should initialize the searchParams with them
    // by default, and the logic only depends on/mutates the filters themselves. This means that the
    // only way we would need to re-add them is if the filter is removed by ItemListing.
    // (Note that we still need to do this in handleSearch to cover cases like clearing params
    // as well.) All this said, this is baking a lot of assumptions about how WorklistItemsSidepanel
    // is loaded and used that aren't the best design. This seems to be a general flaw with this component
    // in that it assumes that it was loaded via a navigation action that pre-populates the searchParams.
    // We should improve the handling of these things as we continue down the listing consolidation path.
    const newSearchParams = resolveItemInWorklistFilter(
      worklistSummary!.id,
      asWorklistType(worklistSummary!.itemType),
      {...props.searchParams, filters}
    );
    props.onUpdateFilters(newSearchParams.filters);
  }

  function decorateWithWorklistInfo(items: Array<ListableItem>): Array<ListableItem> {
    if (!itemIds) {
      return items;
    }
    const positions: {[id: string]: number} = {};
    for (let i = 0; i < itemIds.length; i++) {
      positions[itemIds[i]] = i + 1;
    }

    for (const item of items) {
      item.worklistItemPosition = positions[item.id];
    }

    if (worklistSummary) {
      for (const item of items) {
        item.worklistId = worklistSummary.id;
      }
    }

    return items;
  }

  async function getAllItemIdsInSortOrder(listedItemIds: Array<string>): Promise<Array<string>> {
    if (itemIds!.length > props.searchParams.limit) {
      // If the worklist contains more items than fit on one page, we need to fetch the full list in the
      // currently selected sort order
      const {ids} = (await withLoadingScreen(
        GlobalSearchActions.doGlobalSearch(
          {...props.searchParams, limit: BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST},
          SearchType.WORKLIST_ITEMS,
          'worklists:ITEM_FETCH'
        )
      )) as GlobalSearchResponse;
      return ids;
    } else {
      return listedItemIds;
    }
  }

  async function onSaveOrder(listedItemIds: Array<string>): Promise<void> {
    if (!worklistSummary || !props.searchResults) {
      return;
    }
    assert(Boolean(itemIds), 'Save Order option should not appear when there are no items');
    assert(!props.searchParams.query, 'Save Order option should not appear when there is a search query');

    if (props.searchResults.total !== itemIds!.length) {
      notifications.error(
        'You cannot change the order of this worklist because it contains items that are inaccessible to you.'
      );
      return;
    }

    const allItemIdsInNewOrder = await getAllItemIdsInSortOrder(listedItemIds);
    dispatchUpdateWorklist(
      worklistSummary.id,
      {
        itemIds: allItemIdsInNewOrder,
      },
      {refreshWorklistItemsSearch: true}
    );
  }

  const crumbs = (
    <SplitBreadcrumbs
      crumbs={[
        <CrumbLink
          key="breadcrumbs-home"
          text="Home"
          onClick={(e: BasicEvent) => {
            e.preventDefault();
            SidepanelActions.openSection(NavConstants.Section.HOME);
          }}
        />,
        <CrumbLink
          key="breadcrumbs-worklists"
          text="Worklists"
          onClick={(e: BasicEvent) => {
            e.preventDefault();
            props.clearSelectedWorklist();
          }}
        />,
        worklistSummary ? (
          <CrumbLink key="root" text={worklistSummary.name} />
        ) : (
          <HorizontalThrobber key="root" />
        ),
      ]}
      settingsIcon={<WorklistSettingsIcon worklist={worklistSummary} itemIds={itemIds} />}
    />
  );

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.SIDEPANEL_HOME_WORKLISTS}
        onSearch={worklistSummary ? handleSearch : noop}
        onUpdateFilters={worklistSummary ? handleUpdateFilters : noop}
        onUpdateQueryString={worklistSummary ? props.onUpdateQueryString : noop}
        onUpdatePageSize={worklistSummary ? props.onUpdatePageSize : noop}
        onUpdateOffset={worklistSummary ? props.onUpdateOffset : noop}
        onUpdateSortOrder={worklistSummary ? props.onUpdateSortOrder : noop}
        onUpdateGroupBy={worklistSummary ? props.onUpdateGroupBy : noop}
        availableTypes={
          worklistSummary ? WorklistTypeToModelTypes[asWorklistType(worklistSummary.itemType)] : []
        }
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={() => crumbs}
        extraPrimaryFilterOptions={[]}
        massageItems={decorateWithWorklistInfo}
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        preservedFilterKeys={[FilterKey.ITEM_IN_WORKLIST]}
        section={NavConstants.Section.WORKLIST_ITEMS}
        additionalHeaderButtons={
          worklistSummary && (
            <ScanIntoWorklistButton worklistSummary={worklistSummary} className="u-mini-spacing-left" />
          )
        }
        onSaveOrder={onSaveOrder}
        showMultiKeySortMenu={true}
        worklistSummary={worklistSummary}
      />
    </div>
  );
}

function getWorklistItemsURL(
  prevProps: WorklistItemsSidepanelProps & SearchProps,
  props: WorklistItemsSidepanelProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {url: SearchURLs.getNavigationURL('/worklist-items', props.searchParams, props.filterErrors)};
}

export default withSearching(
  WorklistItemsSidepanel,
  SearchType.WORKLIST_ITEMS,
  NavConstants.Section.WORKLIST_ITEMS,
  {
    getNavigationUpdate: getWorklistItemsURL,
  }
);
